/**
 * Life4Earth V2 — Admin Role Rhythms · Per-task editor tray.
 *
 * DEV-1234 iter 2 (edit) + iter 3 (create + soft-retire). Slide-in tray
 * on the right edge of AdminRoleRhythms. In edit mode, opens on task-row
 * click and PUTs changes back. In create mode, opens from a "+ Add task"
 * button inside a cadence group (which seeds the cadence) or under
 * Standing Duties (which seeds task_kind=standing_duty).
 *
 * Sysadmin-only authoring per CLAUDE.md; server gate at controller layer.
 * English-only (admin UI per CLAUDE.md).
 *
 * Anchor shape support:
 *   - daily               → no anchor field (info note)
 *   - weekly / biweekly   → day-of-week dropdown
 *   - monthly_dom         → day-of-month input
 *   - quarterly           → month-of-quarter + day-of-month
 *   - semiannual          → two months + shared day-of-month
 *   - annual              → month + day-of-month
 *   - monthly_dow / school_term / fiscal_year_end → raw JSON fallback
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  cloneTaskToRole,
  createRoleRhythmTask,
  createRoleRhythmTaskStep,
  deleteRoleRhythmTask,
  deleteRoleRhythmTaskStep,
  fetchAdminCatalog,
  updateRoleRhythmTask,
  updateRoleRhythmTaskStep,
  type AdminCatalogRole,
  type RhythmStepDetail,
  type RhythmTaskDetail,
  type CreateRoleRhythmTaskRequest,
  type UpdateRoleRhythmTaskRequest,
} from "../../api/roleRhythms";
// DEV-1240 P3 — Contact picker pulls the platform-user list once and
// filters client-side. Same pattern SlotEditModal uses; admin user counts
// are small enough for this to stay cheap until a real search endpoint
// lands.
import { fetchAllSecurityUsers, type SecurityUserResponse } from "../../api/security";
import styles from "./TaskEditorTray.module.css";

// ─────────────────────────────────────────────────────────────
// Step row state
// ─────────────────────────────────────────────────────────────
//
// One entry per visible row. In edit mode every row corresponds to a
// persisted step (stepGuid set). In create mode rows are staged until
// the parent task is created, at which point each is POSTed in order.

interface StepRow {
  /** Persisted step_guid. Undefined for create-mode-staged rows. */
  stepGuid?: string;
  /** Stable local key for React + drag-and-drop. Persists across reorders. */
  tempId: string;
  label: string;
  /** DEV-1234 iter 5 follow-up. Optional hyperlink. Empty string = no link. */
  url: string;
  /** DEV-1240. Optional friendly label for the URL. Empty string = no
   *  label set; user-facing surfaces fall back to displaying the URL. */
  urlLabel: string;
  isRequired: boolean;
  /** Per-row in-flight indicator (edit-mode live mutations only). */
  saving?: boolean;
}

let stepTempIdCounter = 0;
function nextStepTempId(): string {
  stepTempIdCounter += 1;
  return `step-tmp-${stepTempIdCounter}`;
}

function rowFromStep(step: RhythmStepDetail): StepRow {
  return {
    stepGuid: step.stepGuid,
    tempId: step.stepGuid,
    label: step.label,
    url: step.url ?? "",
    urlLabel: step.urlLabel ?? "",
    isRequired: step.isRequired,
  };
}

// ─────────────────────────────────────────────────────────────
// Props — discriminated union on mode.
// ─────────────────────────────────────────────────────────────

export type TaskEditorTrayProps =
  | {
      mode: "edit";
      task: RhythmTaskDetail;
      /** DEV-1234 iter 10 — source role context for the Clone task
       *  affordance. When provided, the tray shows a "Clone to another
       *  role" section and filters the target picker to same-pillar
       *  roles (excluding the current role itself). */
      sourceRolePillarType?: string;
      sourceRoleDefinitionGuid?: string;
      open: boolean;
      onClose: () => void;
      onSaved: () => void;
    }
  | {
      mode: "create";
      rhythmGuid: string;
      /** Default task_kind when the tray opens. In create mode the user
       *  can flip between recurring / standing via the segmented control.
       *  In edit mode task_kind is immutable per backend DTO. */
      initialTaskKind?: "recurring_task" | "standing_duty";
      open: boolean;
      onClose: () => void;
      onSaved: () => void;
    };

// ─────────────────────────────────────────────────────────────
// Cadence type metadata
// ─────────────────────────────────────────────────────────────

const CADENCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly_dom", label: "Monthly (by date)" },
  { value: "monthly_dow", label: "Monthly (by weekday)" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "annual", label: "Annual" },
  { value: "school_term", label: "School term" },
  { value: "fiscal_year_end", label: "Fiscal year end" },
];

const DAY_OF_WEEK_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const MONTH_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const FRIENDLY_ANCHOR_TYPES = new Set([
  "daily",
  "weekly",
  "monthly_dom",
  "quarterly",
  "semiannual",
  "annual",
]);

// ─────────────────────────────────────────────────────────────
// Anchor parsing / serialization
// ─────────────────────────────────────────────────────────────

interface AnchorState {
  dayOfWeek?: number;
  dayOfMonth?: number;
  monthOfQuarter?: number;
  months?: number[];
  month?: number;
  /** Raw JSON fallback for cadence types without a friendly form. */
  rawJson?: string;
}

function parseAnchor(cadenceType: string | null | undefined, raw: string | null | undefined): AnchorState {
  if (!raw) return {};
  let parsed: any = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { rawJson: raw };
  }
  if (!parsed || typeof parsed !== "object") {
    return { rawJson: raw };
  }
  const out: AnchorState = {};
  if (typeof parsed.day_of_week === "number") out.dayOfWeek = parsed.day_of_week;
  if (typeof parsed.day_of_month === "number") out.dayOfMonth = parsed.day_of_month;
  if (typeof parsed.month_of_quarter === "number") out.monthOfQuarter = parsed.month_of_quarter;
  if (Array.isArray(parsed.months)) {
    out.months = parsed.months.filter((m: unknown) => typeof m === "number");
  }
  if (typeof parsed.month === "number") out.month = parsed.month;
  if (cadenceType && !FRIENDLY_ANCHOR_TYPES.has(cadenceType)) {
    out.rawJson = raw;
  }
  return out;
}

function serializeAnchor(cadenceType: string, state: AnchorState): string | null {
  switch (cadenceType) {
    case "daily":
      return null;
    case "weekly":
    case "biweekly":
      return JSON.stringify({ day_of_week: state.dayOfWeek ?? 1 });
    case "monthly_dom":
      return JSON.stringify({ day_of_month: state.dayOfMonth ?? 1 });
    case "quarterly":
      return JSON.stringify({
        month_of_quarter: state.monthOfQuarter ?? 1,
        day_of_month: state.dayOfMonth ?? 1,
      });
    case "semiannual":
      return JSON.stringify({
        months: state.months && state.months.length === 2 ? state.months : [1, 7],
        day_of_month: state.dayOfMonth ?? 1,
      });
    case "annual":
      return JSON.stringify({
        month: state.month ?? 1,
        day_of_month: state.dayOfMonth ?? 1,
      });
    default:
      return state.rawJson && state.rawJson.trim() ? state.rawJson : null;
  }
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function TaskEditorTray(props: TaskEditorTrayProps) {
  const isEdit = props.mode === "edit";

  // ── Task kind state ───────────────────────────────────────
  // In edit mode: derived from the existing task, immutable.
  // In create mode: starts at props.initialTaskKind (default recurring),
  // user can flip via the segmented control.
  const [taskKind, setTaskKind] = useState<"recurring_task" | "standing_duty">(
    isEdit
      ? (props.task.taskKind as "recurring_task" | "standing_duty")
      : (props.initialTaskKind ?? "recurring_task"),
  );
  const isRecurring = taskKind === "recurring_task";

  // ── Form state ────────────────────────────────────────────

  const [name, setName] = useState(isEdit ? props.task.name : "");
  const [description, setDescription] = useState(
    isEdit ? (props.task.description ?? "") : "",
  );
  const [cadenceType, setCadenceType] = useState<string>(
    isEdit ? (props.task.cadenceType ?? "weekly") : "weekly",
  );
  const [anchor, setAnchor] = useState<AnchorState>(() =>
    isEdit ? parseAnchor(props.task.cadenceType, props.task.cadenceAnchor) : {},
  );
  const [leadTimeDays, setLeadTimeDays] = useState<number>(
    isEdit ? props.task.leadTimeDays : 14,
  );
  const [dueWindowDays, setDueWindowDays] = useState<number>(
    isEdit ? props.task.dueWindowDays : 7,
  );
  // DEV-1234 iter 8 — sort_order is no longer user-facing. Tray retains
  // no state for it. New tasks default to 0 (backend appends to next
  // available slot); existing tasks reorder via drag on the role detail.

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Retire (soft-delete) state — edit mode only.
  const [confirmingRetire, setConfirmingRetire] = useState(false);
  const [retiring, setRetiring] = useState(false);

  // DEV-1234 iter 10 — clone task state. Edit-mode only.
  const [cloneSectionOpen, setCloneSectionOpen] = useState(false);
  const [cloneTargets, setCloneTargets] = useState<AdminCatalogRole[] | null>(null);
  const [cloneTargetsLoading, setCloneTargetsLoading] = useState(false);
  const [cloneTargetSearch, setCloneTargetSearch] = useState("");
  const [cloningToGuid, setCloningToGuid] = useState<string | null>(null);
  const [cloneSuccessLabel, setCloneSuccessLabel] = useState<string | null>(null);

  // ── Steps state (DEV-1234 iter 5) ─────────────────────────
  // Edit mode: rows are live-mutated against the server (POST on add,
  // PUT on label/required/reorder, DELETE on remove). Step changes
  // persist even if the user closes the tray without clicking Save —
  // a deliberate trade-off vs accumulating an unsaved-diff complexity.
  // Create mode: rows are staged in local state and POSTed in order
  // after the parent task is created on Save.
  const [steps, setSteps] = useState<StepRow[]>(() =>
    isEdit ? props.task.steps.map(rowFromStep) : [],
  );
  const [addingStep, setAddingStep] = useState(false);

  // DEV-1240 P3 — Contact person state. Phase 1 wires only the user-side
  // path; the role-based contact picker ships in a future epic. The
  // resolved name comes from props.task.contactUser (populated by the
  // read endpoint) so we can show the pill immediately on tray open.
  const [contactUserGuid, setContactUserGuid] = useState<string | null>(
    isEdit ? (props.task.contactUserGuid ?? null) : null,
  );
  const [contactUserName, setContactUserName] = useState<string | null>(
    isEdit ? (props.task.contactUser?.name ?? null) : null,
  );
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  // Lazy-loaded platform user list. Same client-side filter pattern as
  // SlotEditModal's user picker.
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[] | null>(null);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  // Native HTML5 drag state — mirrors the UnifiedTeamTab slot reorder
  // pattern. tempId is used as the drag identifier since it's the
  // only id guaranteed for every row in both modes.
  const [draggedStepId, setDraggedStepId] = useState<string | null>(null);
  const [dragOverStepId, setDragOverStepId] = useState<string | null>(null);

  // Reset state when the tray's target changes — different task clicked
  // in edit mode, or tray re-opened in create mode.
  const taskGuidKey = isEdit ? props.task.taskGuid : null;
  const initialKindKey = !isEdit ? props.initialTaskKind ?? "" : "";

  useEffect(() => {
    if (props.mode === "edit") {
      const t = props.task;
      setTaskKind(t.taskKind as "recurring_task" | "standing_duty");
      setName(t.name);
      setDescription(t.description ?? "");
      setCadenceType(t.cadenceType ?? "weekly");
      setAnchor(parseAnchor(t.cadenceType, t.cadenceAnchor));
      setLeadTimeDays(t.leadTimeDays);
      setDueWindowDays(t.dueWindowDays);
      setSteps(t.steps.map(rowFromStep));
      // DEV-1240 P3 — seed contact from the loaded task.
      setContactUserGuid(t.contactUserGuid ?? null);
      setContactUserName(t.contactUser?.name ?? null);
    } else {
      setTaskKind(props.initialTaskKind ?? "recurring_task");
      setName("");
      setDescription("");
      setCadenceType("weekly");
      setAnchor({});
      setLeadTimeDays(14);
      setDueWindowDays(7);
      setSteps([]);
      setContactUserGuid(null);
      setContactUserName(null);
    }
    setError(null);
    setConfirmingRetire(false);
    setDraggedStepId(null);
    setDragOverStepId(null);
    setContactSearchOpen(false);
    setContactSearchQuery("");
    // DEV-1234 iter 10 — reset clone UI on task switch
    setCloneSectionOpen(false);
    setCloneTargets(null);
    setCloneTargetSearch("");
    setCloningToGuid(null);
    setCloneSuccessLabel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskGuidKey, initialKindKey, props.mode]);

  // ── Patch builder (edit mode only) ────────────────────────

  const patch = useMemo<UpdateRoleRhythmTaskRequest>(() => {
    if (props.mode !== "edit") return {};
    const t = props.task;
    const p: UpdateRoleRhythmTaskRequest = {};
    if (name.trim() !== t.name) p.name = name.trim();
    const desc = description.trim() || null;
    if (desc !== (t.description ?? null)) p.description = desc;
    if (isRecurring) {
      if (cadenceType !== t.cadenceType) p.cadenceType = cadenceType;
      const newAnchor = serializeAnchor(cadenceType, anchor);
      if (newAnchor !== (t.cadenceAnchor ?? null)) p.cadenceAnchor = newAnchor;
      if (leadTimeDays !== t.leadTimeDays) p.leadTimeDays = leadTimeDays;
      if (dueWindowDays !== t.dueWindowDays) p.dueWindowDays = dueWindowDays;
    }
    // DEV-1240 P3 — Contact field uses the three-state sentinel:
    //   null            (= omit) → no-op
    //   '00000000…0000' → backend interprets Guid.Empty as "clear"
    //   real GUID       → set to that user
    // Frontend null state means "no contact" — translate to Guid.Empty
    // when the original task HAD a contact (otherwise omit to skip the
    // no-op write).
    const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
    const origContact = (t.contactUserGuid ?? null);
    const currContact = contactUserGuid ?? null;
    if (origContact !== currContact) {
      p.contactUserGuid = currContact ?? EMPTY_GUID;
    }
    // DEV-1234 iter 8 — sort_order edits happen via drag-to-reorder on
    // the role detail, not the tray. The patch never carries sortOrder.
    return p;
  }, [props, name, description, cadenceType, anchor, leadTimeDays, dueWindowDays,
      isRecurring, contactUserGuid]);

  // Save is enabled when: edit-mode-dirty, OR create-mode-with-name.
  const saveEnabled = isEdit ? Object.keys(patch).length > 0 : name.trim().length > 0;

  // ── Step handlers ─────────────────────────────────────────
  //
  // Edit mode = live mutations against the backend per row.
  // Create mode = local-state-only until the parent task is created on Save.
  //
  // The shape of each handler is symmetric across modes (local state
  // updates always happen optimistically); the only difference is
  // whether a server call follows.

  const liveMode = isEdit;
  const editTaskGuid = isEdit ? props.task.taskGuid : null;

  const handleAddStep = useCallback(async () => {
    if (addingStep) return;
    if (liveMode && editTaskGuid) {
      setAddingStep(true);
      setError(null);
      try {
        const created = await createRoleRhythmTaskStep(editTaskGuid, {
          label: "New step",
          isRequired: true,
        });
        setSteps((prev) => [
          ...prev,
          {
            stepGuid: created.stepGuid,
            tempId: created.stepGuid,
            label: created.label,
            url: created.url ?? "",
            urlLabel: created.urlLabel ?? "",
            isRequired: created.isRequired,
          },
        ]);
      } catch (err: any) {
        setError(err?.message || "Failed to add step.");
      } finally {
        setAddingStep(false);
      }
    } else {
      // Create mode — stage locally.
      setSteps((prev) => [
        ...prev,
        {
          tempId: nextStepTempId(),
          label: "New step",
          url: "",
          urlLabel: "",
          isRequired: true,
        },
      ]);
    }
  }, [addingStep, liveMode, editTaskGuid]);

  const handleEditStepLabel = useCallback(
    async (tempId: string, newLabel: string) => {
      const trimmed = newLabel.trim();
      // Optimistic local update first.
      setSteps((prev) => prev.map((s) => (s.tempId === tempId ? { ...s, label: trimmed } : s)));

      if (!liveMode) return; // create mode — staged
      const row = steps.find((s) => s.tempId === tempId);
      if (!row?.stepGuid) return;

      // Note: no "unchanged" short-circuit here. The onChange handler
      // updates `steps` optimistically on every keystroke, so by the
      // time onBlur fires the closure's `steps` already reflects the
      // new value. Any equality check against it would falsely report
      // "unchanged" and silently drop the PUT. We accept the very
      // rare wasted PUT when a user clicks in/out without editing.
      if (!trimmed) {
        // Empty label — show validation. Don't attempt revert (we
        // can't recover the prior persisted value from the optimistic
        // state); the user just retypes the label.
        setError("Step label cannot be empty.");
        return;
      }
      try {
        await updateRoleRhythmTaskStep(row.stepGuid, { label: trimmed });
      } catch (err: any) {
        setError(err?.message || "Failed to save step label.");
      }
    },
    [liveMode, steps],
  );

  const handleEditStepUrl = useCallback(
    async (tempId: string, newUrl: string) => {
      const trimmed = newUrl.trim();
      // Optimistic local update.
      setSteps((prev) => prev.map((s) => (s.tempId === tempId ? { ...s, url: trimmed } : s)));
      if (!liveMode) return;
      const row = steps.find((s) => s.tempId === tempId);
      if (!row?.stepGuid) return;
      // No "unchanged" short-circuit — see comment in handleEditStepLabel.
      // The onChange handler has already mutated `steps` optimistically,
      // so any equality check against the closure would falsely report
      // "no change" and silently drop the PUT (this was the iter 5
      // follow-up bug Skip reported on 2026-06-04).
      try {
        // Empty string clears the URL on the server (controller treats
        // whitespace-only as null per the patch shape).
        await updateRoleRhythmTaskStep(row.stepGuid, { url: trimmed });
      } catch (err: any) {
        setError(err?.message || "Failed to save step URL.");
      }
    },
    [liveMode, steps],
  );

  // DEV-1240. Mirrors handleEditStepUrl — empty string clears the label
  // on the backend; user-facing surfaces then fall back to rendering the
  // raw URL as the link text.
  const handleEditStepUrlLabel = useCallback(
    async (tempId: string, newLabel: string) => {
      const trimmed = newLabel.trim();
      setSteps((prev) => prev.map((s) => (s.tempId === tempId ? { ...s, urlLabel: trimmed } : s)));
      if (!liveMode) return;
      const row = steps.find((s) => s.tempId === tempId);
      if (!row?.stepGuid) return;
      try {
        await updateRoleRhythmTaskStep(row.stepGuid, { urlLabel: trimmed });
      } catch (err: any) {
        setError(err?.message || "Failed to save link label.");
      }
    },
    [liveMode, steps],
  );

  // ── DEV-1240 P3 — Contact picker handlers ────────────────
  //
  // Lazy load the platform-user list the first time the picker opens.
  // We don't preload on tray mount because the contact field is optional
  // and most tasks don't have one — keep the network cost off the
  // common path. Same client-side filter pattern as SlotEditModal.

  const handleOpenContactPicker = useCallback(async () => {
    setContactSearchOpen(true);
    if (allUsers !== null || allUsersLoading) return;
    setAllUsersLoading(true);
    try {
      const list = await fetchAllSecurityUsers();
      setAllUsers(list);
    } catch (err: any) {
      setError(err?.message || "Failed to load users.");
      setContactSearchOpen(false);
    } finally {
      setAllUsersLoading(false);
    }
  }, [allUsers, allUsersLoading]);

  const filteredContactCandidates = useMemo<SecurityUserResponse[]>(() => {
    if (!allUsers) return [];
    const q = contactSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter((u) => u.isActive)
      .filter((u) => {
        const haystack = `${u.displayName ?? ""} ${u.name ?? ""} ${u.email ?? ""}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 25);
  }, [allUsers, contactSearchQuery]);

  const handlePickContact = useCallback((u: SecurityUserResponse) => {
    setContactUserGuid(u.userGuid);
    setContactUserName(u.displayName || u.name || u.email || "(no name)");
    setContactSearchOpen(false);
    setContactSearchQuery("");
  }, []);

  const handleClearContact = useCallback(() => {
    setContactUserGuid(null);
    setContactUserName(null);
  }, []);

  const handleToggleStepRequired = useCallback(
    async (tempId: string) => {
      const row = steps.find((s) => s.tempId === tempId);
      if (!row) return;
      const next = !row.isRequired;
      // Optimistic update.
      setSteps((prev) => prev.map((s) => (s.tempId === tempId ? { ...s, isRequired: next } : s)));
      if (!liveMode || !row.stepGuid) return;
      try {
        await updateRoleRhythmTaskStep(row.stepGuid, { isRequired: next });
      } catch (err: any) {
        setError(err?.message || "Failed to update step.");
        // Revert.
        setSteps((prev) => prev.map((s) => (s.tempId === tempId ? { ...s, isRequired: !next } : s)));
      }
    },
    [liveMode, steps],
  );

  const handleDeleteStep = useCallback(
    async (tempId: string) => {
      const row = steps.find((s) => s.tempId === tempId);
      if (!row) return;
      // Optimistic remove.
      const snapshot = steps;
      setSteps((prev) => prev.filter((s) => s.tempId !== tempId));
      if (!liveMode || !row.stepGuid) return;
      try {
        await deleteRoleRhythmTaskStep(row.stepGuid);
      } catch (err: any) {
        setError(err?.message || "Failed to delete step.");
        // Restore.
        setSteps(snapshot);
      }
    },
    [liveMode, steps],
  );

  // ── Drag-and-drop reorder ─────────────────────────────────

  const handleDragStart = useCallback(
    (tempId: string) => (e: React.DragEvent) => {
      setDraggedStepId(tempId);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", tempId);
    },
    [],
  );

  const handleDragOver = useCallback(
    (tempId: string) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverStepId(tempId);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverStepId(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedStepId(null);
    setDragOverStepId(null);
  }, []);

  const handleDrop = useCallback(
    (targetTempId: string) => async (e: React.DragEvent) => {
      e.preventDefault();
      setDraggedStepId(null);
      setDragOverStepId(null);

      const sourceTempId = e.dataTransfer.getData("text/plain");
      if (!sourceTempId || sourceTempId === targetTempId) return;

      const sourceIdx = steps.findIndex((s) => s.tempId === sourceTempId);
      const targetIdx = steps.findIndex((s) => s.tempId === targetTempId);
      if (sourceIdx === -1 || targetIdx === -1) return;

      const reordered = [...steps];
      const [moved] = reordered.splice(sourceIdx, 1);
      reordered.splice(targetIdx, 0, moved);
      setSteps(reordered);

      // Live mode — PUT each affected row's new sort_order. We compute
      // the diff by index so only rows that actually moved hit the API.
      if (!liveMode) return;
      const updates = reordered
        .map((row, idx) => ({ row, newSortOrder: idx }))
        .filter(({ row, newSortOrder }) => {
          const origIdx = steps.findIndex((s) => s.tempId === row.tempId);
          return origIdx !== newSortOrder;
        });

      try {
        await Promise.all(
          updates.map(({ row, newSortOrder }) =>
            row.stepGuid
              ? updateRoleRhythmTaskStep(row.stepGuid, { sortOrder: newSortOrder })
              : Promise.resolve(),
          ),
        );
      } catch (err: any) {
        setError(err?.message || "Failed to save reorder.");
        // Revert the visual reorder on failure.
        setSteps(steps);
      }
    },
    [liveMode, steps],
  );

  // ── Save handler ──────────────────────────────────────────

  const handleSave = async () => {
    if (!saveEnabled || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (props.mode === "edit") {
        await updateRoleRhythmTask(props.task.taskGuid, patch);
      } else {
        // DEV-1240 defensive guard. If any caller ever passes a non-string
        // (e.g. a SyntheticEvent) as initialTaskKind, JSON.stringify on the
        // outbound request body would explode with a circular-ref error
        // pointing at "HTMLButtonElement → __reactFiber → FiberNode". That's
        // an opaque message for the user and a frustrating bug for the
        // engineer. Fail loud here instead, with an actionable message.
        if (taskKind !== "recurring_task" && taskKind !== "standing_duty") {
          throw new Error(
            "Task kind is missing or invalid. Pick Recurring or Standing duty and try again.",
          );
        }
        const req: CreateRoleRhythmTaskRequest = {
          taskKind,
          name: name.trim(),
          description: description.trim() || null,
          // sortOrder omitted — backend appends to next available slot
          // (DEV-1234 iter 8: reordering is drag-driven, not numeric).
        };
        if (taskKind === "recurring_task") {
          req.cadenceType = cadenceType;
          req.cadenceAnchor = serializeAnchor(cadenceType, anchor);
          req.leadTimeDays = leadTimeDays;
          req.dueWindowDays = dueWindowDays;
        }
        // DEV-1240 P3 — include contact when set; omit when null so the
        // create endpoint treats the column as unset.
        if (contactUserGuid) {
          req.contactUserGuid = contactUserGuid;
        }
        const createdTask = await createRoleRhythmTask(props.rhythmGuid, req);

        // DEV-1234 iter 5 — persist any staged steps under the new task.
        // We pass an explicit sort_order so the backend doesn't fight the
        // user's drag-ordered sequence. Empty-label rows are dropped on
        // the floor — that's the placeholder default and we don't want
        // to commit junk.
        const stagedSteps = steps.filter((s) => s.label.trim().length > 0);
        for (let i = 0; i < stagedSteps.length; i += 1) {
          const s = stagedSteps[i];
          await createRoleRhythmTaskStep(createdTask.taskGuid, {
            label: s.label.trim(),
            url: s.url.trim() || null,
            urlLabel: s.urlLabel.trim() || null, // DEV-1240
            isRequired: s.isRequired,
            sortOrder: i,
          });
        }
      }
      props.onSaved();
      props.onClose();
    } catch (err: any) {
      setError(err?.message || "Save failed. Check the values and try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Clone task handlers ───────────────────────────────────
  //
  // Opening the clone section lazy-loads the admin catalog and filters
  // to target roles in the same pillar (excluding the source role).
  // Clicking a target row fires the clone POST; on success the picker
  // stays open with a "Cloned to X" confirmation so the admin can pick
  // another target without re-opening (e.g., clone Annual Meeting to
  // President, then VP, then Secretary in a single tray session).

  const sourcePillar = props.mode === "edit" ? props.sourceRolePillarType : undefined;
  const sourceDefinitionGuid = props.mode === "edit" ? props.sourceRoleDefinitionGuid : undefined;
  const canClone = isEdit && !!sourcePillar && !!sourceDefinitionGuid;

  const handleOpenCloneSection = useCallback(async () => {
    if (!canClone || cloneTargetsLoading) return;
    setCloneSectionOpen(true);
    setCloneSuccessLabel(null);
    if (cloneTargets !== null) return; // already loaded
    setCloneTargetsLoading(true);
    setError(null);
    try {
      const catalog = await fetchAdminCatalog();
      // Filter to roles in the same pillar, excluding self.
      const pillar = catalog.pillars.find((p) => p.pillarType === sourcePillar);
      const targets = (pillar?.roles ?? []).filter(
        (r) => r.definitionGuid !== sourceDefinitionGuid,
      );
      setCloneTargets(targets);
    } catch (err: any) {
      setError(err?.message || "Failed to load target roles.");
      setCloneSectionOpen(false);
    } finally {
      setCloneTargetsLoading(false);
    }
  }, [canClone, cloneTargets, cloneTargetsLoading, sourcePillar, sourceDefinitionGuid]);

  const handleCloneTo = useCallback(
    async (target: AdminCatalogRole) => {
      if (props.mode !== "edit" || cloningToGuid) return;
      setCloningToGuid(target.definitionGuid);
      setError(null);
      setCloneSuccessLabel(null);
      try {
        await cloneTaskToRole(props.task.taskGuid, target.definitionGuid);
        setCloneSuccessLabel(target.label);
      } catch (err: any) {
        setError(err?.message || `Failed to clone task to ${target.label}.`);
      } finally {
        setCloningToGuid(null);
      }
    },
    [cloningToGuid, props],
  );

  // ── Retire handler ────────────────────────────────────────

  const handleRetire = async () => {
    if (props.mode !== "edit" || retiring) return;
    setRetiring(true);
    setError(null);
    try {
      await deleteRoleRhythmTask(props.task.taskGuid);
      props.onSaved();
      props.onClose();
    } catch (err: any) {
      setError(err?.message || "Retire failed.");
      setRetiring(false);
      setConfirmingRetire(false);
    }
  };

  if (!props.open) return null;

  const busy = saving || retiring;

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={() => { if (!busy) props.onClose(); }}
        aria-hidden="true"
      />
      <aside className={styles.tray} role="dialog" aria-labelledby="task-editor-title">
        <header className={styles.header}>
          <div>
            <div className={styles.eyebrow}>
              {isRecurring ? "Recurring task" : "Standing duty"}
            </div>
            <h2 id="task-editor-title" className={styles.title}>
              {isEdit ? "Edit task" : "New task"}
            </h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={props.onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.body}>
          {/* ── Task kind (create mode only — immutable in edit) ── */}
          {!isEdit && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Task kind</span>
              <div className={styles.segmentedControl} role="radiogroup" aria-label="Task kind">
                <button
                  type="button"
                  role="radio"
                  aria-checked={taskKind === "recurring_task"}
                  className={
                    taskKind === "recurring_task"
                      ? `${styles.segmentedOption} ${styles.segmentedOptionActive}`
                      : styles.segmentedOption
                  }
                  onClick={() => setTaskKind("recurring_task")}
                >
                  Recurring
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={taskKind === "standing_duty"}
                  className={
                    taskKind === "standing_duty"
                      ? `${styles.segmentedOption} ${styles.segmentedOptionActive}`
                      : styles.segmentedOption
                  }
                  onClick={() => setTaskKind("standing_duty")}
                >
                  Standing duty
                </button>
              </div>
              <span className={styles.fieldHint}>
                {taskKind === "recurring_task"
                  ? "Generates a new instance each cadence period (weekly, monthly, etc.)."
                  : "An ongoing responsibility with no cadence — no instances are generated."}
              </span>
            </div>
          )}

          {/* ── Name ────────────────────────────────────────── */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Name</span>
            <input
              type="text"
              className={styles.textInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus={!isEdit}
            />
          </label>

          {/* ── Description ─────────────────────────────────── */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Description</span>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="What the person holding this role should do, and when."
            />
          </label>

          {/* ── Cadence (recurring only) ────────────────────── */}
          {isRecurring && (
            <>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Cadence</span>
                <select
                  className={styles.select}
                  value={cadenceType}
                  onChange={(e) => setCadenceType(e.target.value)}
                >
                  {CADENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <AnchorSubForm
                cadenceType={cadenceType}
                anchor={anchor}
                onChange={setAnchor}
              />

              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Lead time (days)</span>
                  <input
                    type="number"
                    min={0}
                    className={styles.textInput}
                    value={leadTimeDays}
                    onChange={(e) => setLeadTimeDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  />
                  <span className={styles.fieldHint}>
                    Days before the due date the instance appears in the user's queue.
                  </span>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Due window (days)</span>
                  <input
                    type="number"
                    min={0}
                    className={styles.textInput}
                    value={dueWindowDays}
                    onChange={(e) => setDueWindowDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  />
                  <span className={styles.fieldHint}>
                    Grace days after the due date before the instance flips to overdue.
                  </span>
                </label>
              </div>
            </>
          )}

          {/* ── Steps section ─────────────────────────────────
              The checklist of sub-actions required to complete this
              task. Drag the handle to reorder. */}
          <div className={styles.stepsSection}>
            <div className={styles.stepsHeader}>
              <span className={styles.fieldLabel}>Steps</span>
              {isEdit && (
                <span className={styles.stepsHint}>
                  Step changes save automatically.
                </span>
              )}
            </div>

            {steps.length === 0 && (
              <div className={styles.stepsEmpty}>
                No steps yet. Steps break a task into a checklist that the
                person doing the work checks off as they go.
              </div>
            )}

            {steps.map((step) => {
              const isDragged = draggedStepId === step.tempId;
              const isDragOver = dragOverStepId === step.tempId && draggedStepId !== step.tempId;
              return (
                <div
                  key={step.tempId}
                  className={
                    isDragged
                      ? `${styles.stepRow} ${styles.stepRowDragging}`
                      : isDragOver
                      ? `${styles.stepRow} ${styles.stepRowDragOver}`
                      : styles.stepRow
                  }
                  draggable={true}
                  onDragStart={handleDragStart(step.tempId)}
                  onDragOver={handleDragOver(step.tempId)}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop(step.tempId)}
                >
                  <span className={styles.stepDragHandle} aria-hidden="true">
                    ⋮⋮
                  </span>
                  <div className={styles.stepBody}>
                    <div className={styles.stepBodyTop}>
                      <input
                        type="text"
                        className={styles.stepLabelInput}
                        value={step.label}
                        onChange={(e) => {
                          // Local optimistic update only — defer the API
                          // call to onBlur to avoid a PUT per keystroke.
                          const next = e.target.value;
                          setSteps((prev) =>
                            prev.map((s) => (s.tempId === step.tempId ? { ...s, label: next } : s)),
                          );
                        }}
                        onBlur={(e) => {
                          void handleEditStepLabel(step.tempId, e.target.value);
                        }}
                        maxLength={200}
                        placeholder="Step description"
                      />
                      <label className={styles.stepRequiredToggle} title="Required to complete the task">
                        <input
                          type="checkbox"
                          checked={step.isRequired}
                          onChange={() => void handleToggleStepRequired(step.tempId)}
                        />
                        <span>Required</span>
                      </label>
                      <button
                        type="button"
                        className={styles.stepDeleteBtn}
                        onClick={() => void handleDeleteStep(step.tempId)}
                        aria-label="Delete step"
                        title="Delete step"
                      >
                        ×
                      </button>
                    </div>
                    {/* URL line — DEV-1234 iter 5 follow-up. Always rendered
                        with a 🔗 icon prefix. When a value is set, the icon
                        becomes a clickable link-out (opens the URL in a new
                        tab). The text input stays editable in both states. */}
                    <div className={styles.stepBodyUrl}>
                      {step.url.trim() ? (
                        <a
                          className={styles.stepUrlOpenLink}
                          href={step.url.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={step.url.trim()}
                          aria-label={step.urlLabel.trim()
                            ? `Open ${step.urlLabel.trim()} in new tab`
                            : "Open link in new tab"}
                        >
                          🔗
                        </a>
                      ) : (
                        <span
                          className={styles.stepUrlIconDim}
                          aria-hidden="true"
                          title="No link set"
                        >
                          🔗
                        </span>
                      )}
                      {/* DEV-1240 — Label input next to URL. Empty = no
                          label set, user-facing surfaces fall back to the
                          raw URL. Width is constrained so the row keeps
                          its single-line shape on standard tray widths. */}
                      <input
                        type="text"
                        className={styles.stepUrlLabelInput}
                        value={step.urlLabel}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSteps((prev) =>
                            prev.map((s) => (s.tempId === step.tempId ? { ...s, urlLabel: next } : s)),
                          );
                        }}
                        onBlur={(e) => {
                          void handleEditStepUrlLabel(step.tempId, e.target.value);
                        }}
                        maxLength={200}
                        placeholder="Link label (optional)"
                      />
                      <span className={styles.stepUrlArrow} aria-hidden="true">→</span>
                      <input
                        type="url"
                        className={styles.stepUrlInput}
                        value={step.url}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSteps((prev) =>
                            prev.map((s) => (s.tempId === step.tempId ? { ...s, url: next } : s)),
                          );
                        }}
                        onBlur={(e) => {
                          void handleEditStepUrl(step.tempId, e.target.value);
                        }}
                        maxLength={2048}
                        placeholder="https://… (optional link)"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className={styles.stepAddBtn}
              onClick={() => void handleAddStep()}
              disabled={addingStep}
            >
              + {addingStep ? "Adding…" : "Add step"}
            </button>
          </div>

          {/* ── DEV-1240 P3 — Contact person ──────────────────────
              Optional. When set, user-facing surfaces (Action Center,
              RhythmTaskTray) render the contact + a Message button that
              opens an in-app DM. Picker is lazy-loaded — only fetches the
              user list on first open. */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Contact</span>
            {contactUserGuid && contactUserName ? (
              <div className={styles.contactPill}>
                <span className={styles.contactPillName}>{contactUserName}</span>
                <button
                  type="button"
                  className={styles.contactPillClear}
                  onClick={handleClearContact}
                  aria-label="Clear contact"
                  title="Clear contact"
                >
                  ×
                </button>
              </div>
            ) : !contactSearchOpen ? (
              <button
                type="button"
                className={styles.contactAddBtn}
                onClick={() => void handleOpenContactPicker()}
              >
                + Add contact
              </button>
            ) : (
              <div className={styles.contactSearchBox}>
                <input
                  type="text"
                  className={styles.textInput}
                  autoFocus
                  value={contactSearchQuery}
                  onChange={(e) => setContactSearchQuery(e.target.value)}
                  placeholder={allUsersLoading ? "Loading users…" : "Search by name or email…"}
                  disabled={allUsersLoading}
                />
                {contactSearchQuery.trim() && (
                  <ul className={styles.contactResults}>
                    {filteredContactCandidates.length === 0 ? (
                      <li className={styles.contactResultEmpty}>No matches.</li>
                    ) : (
                      filteredContactCandidates.map((u) => (
                        <li key={u.userGuid}>
                          <button
                            type="button"
                            className={styles.contactResultRow}
                            onClick={() => handlePickContact(u)}
                          >
                            <span className={styles.contactResultName}>
                              {u.displayName || u.name || u.email}
                            </span>
                            {u.email && (
                              <span className={styles.contactResultEmail}>{u.email}</span>
                            )}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
                <button
                  type="button"
                  className={styles.contactCancelBtn}
                  onClick={() => {
                    setContactSearchOpen(false);
                    setContactSearchQuery("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <span className={styles.fieldHint}>
              Optional. The assignee will see this person's name with a Message button.
            </span>
          </div>
        </div>

        {/* ── Clone section (edit mode + source role context provided) ──
            Filtered to same-pillar target roles, excluding the current
            role. Picker stays open after each clone so the admin can
            send the same task to multiple targets in one tray session. */}
        {isEdit && canClone && cloneSectionOpen && (
          <div className={styles.cloneSection}>
            <div className={styles.cloneSectionHeader}>
              <span>Clone this task to another role in the same pillar</span>
              <button
                type="button"
                className={styles.cloneSectionCloseBtn}
                onClick={() => {
                  setCloneSectionOpen(false);
                  setCloneSuccessLabel(null);
                }}
                aria-label="Close clone picker"
              >
                ×
              </button>
            </div>
            {cloneSuccessLabel && (
              <div className={styles.cloneSuccess}>
                ✓ Cloned to <strong>{cloneSuccessLabel}</strong>. Pick another
                target or close.
              </div>
            )}
            {cloneTargetsLoading && (
              <div className={styles.cloneEmpty}>Loading target roles…</div>
            )}
            {!cloneTargetsLoading && cloneTargets !== null && (
              <>
                <input
                  type="search"
                  className={styles.cloneTargetSearch}
                  placeholder="Search target roles…"
                  value={cloneTargetSearch}
                  onChange={(e) => setCloneTargetSearch(e.target.value)}
                />
                <div className={styles.cloneTargetList}>
                  {cloneTargets
                    .filter((t) =>
                      cloneTargetSearch.trim() === ""
                        ? true
                        : t.label.toLowerCase().includes(cloneTargetSearch.trim().toLowerCase()),
                    )
                    .map((target) => (
                      <button
                        key={target.definitionGuid}
                        type="button"
                        className={styles.cloneTargetRow}
                        onClick={() => void handleCloneTo(target)}
                        disabled={cloningToGuid !== null}
                      >
                        <span className={styles.cloneTargetLabel}>{target.label}</span>
                        <span className={styles.cloneTargetMeta}>
                          {target.roleCategory}
                          {cloningToGuid === target.definitionGuid && " · Cloning…"}
                        </span>
                      </button>
                    ))}
                  {cloneTargets.length === 0 && (
                    <div className={styles.cloneEmpty}>
                      No other roles in this pillar to clone to.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Retire confirm strip (edit mode only) ─────────── */}
        {isEdit && confirmingRetire && (
          <div className={styles.retireConfirm}>
            <p className={styles.retireConfirmText}>
              <strong>Retire this task?</strong> It stops generating future
              instances. Existing open instances continue. Audit history and
              completed instances are preserved.
            </p>
            <div className={styles.retireConfirmActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setConfirmingRetire(false)}
                disabled={retiring}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={handleRetire}
                disabled={retiring}
              >
                {retiring ? "Retiring…" : "Retire task"}
              </button>
            </div>
          </div>
        )}

        <footer className={styles.footer}>
          {isEdit && canClone && !confirmingRetire && (
            <button
              type="button"
              className={styles.btnLinkAccent}
              onClick={() => void handleOpenCloneSection()}
              disabled={busy || cloneSectionOpen}
              title="Clone this task into another role's rhythm"
            >
              Clone to…
            </button>
          )}
          {isEdit && !confirmingRetire && (
            <button
              type="button"
              className={styles.btnLinkDanger}
              onClick={() => setConfirmingRetire(true)}
              disabled={busy}
            >
              Retire task
            </button>
          )}
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={props.onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={!saveEnabled || busy}
            >
              {saving ? "Saving…" : (isEdit ? "Save" : "Create task")}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Anchor sub-forms
// ─────────────────────────────────────────────────────────────

function AnchorSubForm({
  cadenceType,
  anchor,
  onChange,
}: {
  cadenceType: string;
  anchor: AnchorState;
  onChange: (next: AnchorState) => void;
}) {
  if (cadenceType === "daily") {
    return (
      <div className={styles.anchorInfo}>
        Daily tasks have no anchor — instances materialize every day.
      </div>
    );
  }

  if (cadenceType === "weekly" || cadenceType === "biweekly") {
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Day of week</span>
        <select
          className={styles.select}
          value={anchor.dayOfWeek ?? 1}
          onChange={(e) => onChange({ ...anchor, dayOfWeek: parseInt(e.target.value, 10) })}
        >
          {DAY_OF_WEEK_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </label>
    );
  }

  if (cadenceType === "monthly_dom") {
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Day of month</span>
        <input
          type="number"
          min={1}
          max={31}
          className={styles.textInput}
          value={anchor.dayOfMonth ?? 1}
          onChange={(e) => onChange({ ...anchor, dayOfMonth: clamp(parseInt(e.target.value, 10), 1, 31) })}
        />
        <span className={styles.fieldHint}>
          1–31. Months without that date use the last day (e.g., day 31 → Feb 28).
        </span>
      </label>
    );
  }

  if (cadenceType === "quarterly") {
    return (
      <div className={styles.fieldRow}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Month of quarter</span>
          <select
            className={styles.select}
            value={anchor.monthOfQuarter ?? 1}
            onChange={(e) => onChange({ ...anchor, monthOfQuarter: parseInt(e.target.value, 10) })}
          >
            <option value={1}>1st month</option>
            <option value={2}>2nd month</option>
            <option value={3}>3rd month</option>
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Day of month</span>
          <input
            type="number"
            min={1}
            max={31}
            className={styles.textInput}
            value={anchor.dayOfMonth ?? 1}
            onChange={(e) => onChange({ ...anchor, dayOfMonth: clamp(parseInt(e.target.value, 10), 1, 31) })}
          />
        </label>
      </div>
    );
  }

  if (cadenceType === "semiannual") {
    const months = anchor.months && anchor.months.length === 2 ? anchor.months : [1, 7];
    const setMonth = (idx: 0 | 1, value: number) => {
      const next = [...months];
      next[idx] = value;
      onChange({ ...anchor, months: next });
    };
    return (
      <>
        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>First month</span>
            <select
              className={styles.select}
              value={months[0]}
              onChange={(e) => setMonth(0, parseInt(e.target.value, 10))}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Second month</span>
            <select
              className={styles.select}
              value={months[1]}
              onChange={(e) => setMonth(1, parseInt(e.target.value, 10))}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
        </div>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Day of month</span>
          <input
            type="number"
            min={1}
            max={31}
            className={styles.textInput}
            value={anchor.dayOfMonth ?? 1}
            onChange={(e) => onChange({ ...anchor, dayOfMonth: clamp(parseInt(e.target.value, 10), 1, 31) })}
          />
        </label>
      </>
    );
  }

  if (cadenceType === "annual") {
    return (
      <div className={styles.fieldRow}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Month</span>
          <select
            className={styles.select}
            value={anchor.month ?? 1}
            onChange={(e) => onChange({ ...anchor, month: parseInt(e.target.value, 10) })}
          >
            {MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Day of month</span>
          <input
            type="number"
            min={1}
            max={31}
            className={styles.textInput}
            value={anchor.dayOfMonth ?? 1}
            onChange={(e) => onChange({ ...anchor, dayOfMonth: clamp(parseInt(e.target.value, 10), 1, 31) })}
          />
        </label>
      </div>
    );
  }

  // Fallback for monthly_dow / school_term / fiscal_year_end.
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>Anchor (raw JSON)</span>
      <textarea
        className={styles.textarea}
        rows={3}
        value={anchor.rawJson ?? ""}
        onChange={(e) => onChange({ ...anchor, rawJson: e.target.value })}
        placeholder='{"key": "value"}'
        spellCheck={false}
      />
      <span className={styles.fieldHint}>
        This cadence type has no friendly form yet. Provide the anchor JSON
        directly. Leave blank to clear the anchor.
      </span>
    </label>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}
